<script setup>
import { ref, watch } from 'vue'
import { Head, Link, router, usePage } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Button } from '@/Components/ui/button'
import { Input } from '@/Components/ui/input'
import { Badge } from '@/Components/ui/badge'
import { Card } from '@/Components/ui/card'
import { debounce } from 'lodash'
import teacher from '@/routes/teacher'

const props = defineProps({
  contents: Object,
  filters: Object,
  subjects: Array,
  examboards: Array,
  levels: Array,
})

const search = ref(props.filters.search || '')
const subjectId = ref(props.filters.subject_id || '')
const examboardId = ref(props.filters.examboard_id || '')
const levelId = ref(props.filters.level_id || '')
const status = ref(props.filters.status || '')
const contentType = ref(props.filters.content_type || '')

const updateFilters = debounce(() => {
  router.get(
    teacher.content.index(),
    {
      search: search.value,
      subject_id: subjectId.value,
      examboard_id: examboardId.value,
      level_id: levelId.value,
      status: status.value,
      content_type: contentType.value,
    },
    { preserveState: true, replace: true }
  )
}, 300)

watch([search, subjectId, examboardId, levelId, status, contentType], () => {
    updateFilters()
})

const clearFilters = () => {
  search.value = ''
  subjectId.value = ''
  examboardId.value = ''
  levelId.value = ''
  status.value = ''
  contentType.value = ''
}

const getStatusColor = (status) => {
    switch (status) {
        case 'published': return 'bg-green-500 hover:bg-green-600';
        case 'draft': return 'bg-gray-500 hover:bg-gray-600';
        case 'archived': return 'bg-amber-500 hover:bg-amber-600';
        default: return 'bg-gray-500';
    }
}

const getStatusLabel = (status) => {
    switch (status) {
        case 'published': return 'Published';
        case 'draft': return 'Draft';
        case 'archived': return 'Archived';
        default: return status;
    }
}
</script>

<template>
  <Head title="My Content" />

  <DashboardLayout>
    <div class="">
      <div class="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">My Content</h1>
          <p class="text-gray-500 text-sm mt-1">Manage your video lessons and materials</p>
        </div>
        <Link :href="teacher.content.create()">
          <Button class="bg-blue-600 hover:bg-blue-700 text-white">
            <span class="material-symbols-outlined mr-2">add</span> Create Content
          </Button>
        </Link>
      </div>

      <!-- Content Type Toggle -->
      <div class="inline-flex items-center mb-4 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          @click="contentType = ''"
          :class="[
            'px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5',
            !contentType ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-200' : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          All Content
        </button>
        <button
          @click="contentType = 'video'"
          :class="[
            'px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5',
            contentType === 'video' ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-200' : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          <span class="material-symbols-outlined text-sm">videocam</span>
          Videos
        </button>
        <button
          @click="contentType = 'document'"
          :class="[
            'px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5',
            contentType === 'document' ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-200' : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          <span class="material-symbols-outlined text-sm">description</span>
          Learning Materials
        </button>
      </div>

      <!-- Filters -->
      <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-8">
        <div class="space-y-4">
          <!-- Row 1: Search -->
          <div class="relative">
             <span class="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">search</span>
            <Input
              v-model="search"
              type="text"
              placeholder="Search by title, topic or keywords..."
              class="pl-10"
            />
          </div>

          <!-- Row 2: Filters -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <select v-model="subjectId" class="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
              <option value="">All Subjects</option>
              <option v-for="subject in subjects" :key="subject.id" :value="subject.id">{{ subject.name }}</option>
            </select>

            <select v-model="examboardId" class="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
              <option value="">All Exam Boards</option>
              <option v-for="board in examboards" :key="board.id" :value="board.id">{{ board.name }}</option>
            </select>

            <select v-model="levelId" class="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
              <option value="">All Levels</option>
              <option v-for="level in levels" :key="level.id" :value="level.id">{{ level.name }}</option>
            </select>
          </div>

          <div v-if="search || subjectId || examboardId || levelId || contentType" class="flex justify-end pt-2">
            <button @click="clearFilters" class="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center">
              <span class="material-symbols-outlined mr-1 text-sm">close</span> Clear Filters
            </button>
          </div>
        </div>
      </div>

      <!-- Content Grid -->
      <div v-if="contents.data.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div v-for="content in contents.data" :key="content.id" class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group flex flex-col h-full">
            <!-- Thumbnail / Cover -->
            <div class="relative aspect-video group max-h-48" :class="content.type === 2 ? 'bg-orange-50' : 'bg-gray-100'">
                <!-- Video thumbnail -->
                <template v-if="content.type === 1">
                  <img v-if="content.thumbnail_url" :src="content.thumbnail_url" :alt="content.title" class="object-cover w-full h-full absolute inset-0">
                  <div v-else class="w-full h-full flex flex-col items-center justify-center text-gray-300">
                      <span class="material-symbols-outlined text-4xl mb-2">image</span>
                      <span class="text-xs">No Thumbnail</span>
                  </div>
                </template>

                <!-- Document cover -->
                <template v-else-if="content.type === 2">
                  <div class="w-full h-full flex flex-col items-center justify-center">
                      <div class="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mb-2">
                        <span class="material-symbols-outlined text-3xl text-orange-600">picture_as_pdf</span>
                      </div>
                      <span class="text-xs font-medium text-orange-700">PDF Document</span>
                      <span v-if="content.attachments?.[0]?.original_name" class="text-[10px] text-orange-500 mt-0.5 px-4 truncate max-w-full">{{ content.attachments[0].original_name }}</span>
                  </div>
                </template>

                <!-- Status Badge -->
                <div class="absolute top-2 right-2 z-10">
                    <Badge :class="getStatusColor(content.status)">{{ getStatusLabel(content.status) }}</Badge>
                </div>

                <!-- Content Type Badge -->
                <div class="absolute top-2 left-2 z-10">
                    <span
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      :class="content.type === 2 ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'"
                    >
                      <span class="material-symbols-outlined text-[10px]">{{ content.type === 2 ? 'description' : 'videocam' }}</span>
                      {{ content.type === 2 ? 'MATERIAL' : 'VIDEO' }}
                    </span>
                </div>

                 <!-- Duration (video only) -->
                 <div v-if="content.type === 1 && content.duration" class="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded flex items-center whitespace-nowrap z-10">
                    <span class="material-symbols-outlined text-[10px] mr-1">play_arrow</span>
                    {{ content.formatted_duration || content.duration + 's' }}
                </div>

                 <!-- Attachment indicator for videos with supporting documents -->
                 <div v-if="content.type === 1 && content.attachments?.length > 0" class="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded flex items-center whitespace-nowrap z-10" title="Has supporting document">
                    <span class="material-symbols-outlined text-[10px] mr-1">attach_file</span>
                    PDF
                </div>

                <!-- Hover Overlay -->
                <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[1px]" :class="content.type === 2 ? 'bg-orange-900/40' : 'bg-black/40'">
                     <Link :href="teacher.content.edit(content.id)" class="w-10 h-10 flex items-center justify-center bg-white text-gray-900 rounded-full hover:bg-blue-600 hover:text-white transition-colors shadow-lg" title="Edit">
                        <span class="material-symbols-outlined text-xl">edit</span>
                    </Link>
                    <Link :href="teacher.content.show(content.id)" class="w-10 h-10 flex items-center justify-center bg-white text-gray-900 rounded-full hover:bg-blue-600 hover:text-white transition-colors shadow-lg" title="View">
                         <span class="material-symbols-outlined text-xl">visibility</span>
                    </Link>
                </div>
            </div>

            <!-- Details -->
            <div class="p-4 flex flex-col flex-1">
                <div class="mb-2 min-h-0">
                    <div class="flex flex-wrap gap-1 mb-2">
                        <span v-if="content.subject" class="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100 text-[10px] font-medium truncate max-w-full">{{ content.subject.name }}</span>
                        <span v-if="content.level" class="bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200 text-[10px] font-medium truncate max-w-full">{{ content.level.name }}</span>
                    </div>

                    <h3 class="text-sm font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-blue-600 transition-colors">
                        <Link :href="teacher.content.edit(content.id)">{{ content.title }}</Link>
                    </h3>
                </div>

                 <div class="mt-auto pt-3 border-t border-gray-50 flex items-center justify-between text-xs text-gray-400">
                    <div class="flex items-center space-x-3">
                         <div class="flex items-center" title="Total Views">
                            <span class="material-symbols-outlined text-[14px] mr-1">visibility</span> {{ content.view_count }}
                        </div>
                        <div class="flex items-center" title="Comments">
                            <span class="material-symbols-outlined text-[14px] mr-1">comment</span> {{ content.comments_count || 0 }}
                        </div>
                    </div>
                    <div>
                         {{ content.created_at_human || 'Recently' }}
                    </div>
                 </div>
            </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="col-span-full py-16 text-center text-gray-400 bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200">
         <div class="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
             <span class="material-symbols-outlined text-2xl text-gray-300">search</span>
         </div>
         <h3 class="text-lg font-medium text-gray-900 mb-1">No content found</h3>
         <p class="text-sm text-gray-500 max-w-sm mx-auto mb-6">
             We couldn't find any content matching your current filters. Try adjusting them or search for something else.
         </p>
         <button @click="clearFilters" class="text-blue-600 font-semibold hover:underline text-sm">
             Clear all filters
         </button>
      </div>

      <!-- Pagination -->
       <div v-if="contents.links.length > 3" class="mt-8 flex justify-center">
            <div class="flex flex-wrap -mb-1">
                <template v-for="(link, key) in contents.links" :key="key">
                    <div v-if="link.url === null" class="mr-1 mb-1 px-4 py-3 text-sm leading-4 text-gray-400 border rounded" v-html="link.label" />
                    <Link v-else class="mr-1 mb-1 px-4 py-3 text-sm leading-4 border rounded hover:bg-white focus:border-indigo-500 focus:text-indigo-500" :class="{ 'bg-blue-600 text-white': link.active }" :href="link.url" v-html="link.label" />
                </template>
            </div>
       </div>

    </div>
  </DashboardLayout>
</template>
